import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { isEmpty } from 'lodash-es';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import { GTMService } from '@services';
import { SearchStore, SearchType } from '@store';

import { SearchService } from '../../../search.service';

@Component({
  selector: 'app-gateway-search',
  templateUrl: './gateway-search.component.html',
  styleUrls: ['./gateway-search.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GatewaySearchComponent implements OnInit, OnDestroy {
  gatewaySerial = '';
  boilerSerial = '';
  gatewaySerialValidationErrorPresent = false;
  boilerSerialValidationErrorPresent = false;

  private ngUnsubscribe$: Subject<void> = new Subject<void>();
  private readonly serialRegExp = /^[a-z0-9\\-_]{1,36}$/;

  constructor(
    private searchService: SearchService,
    private searchStore: SearchStore,
    private cdr: ChangeDetectorRef,
    public gtmService: GTMService
  ) {}

  ngOnInit(): void {
    this.searchService.gatewaySearchParams$
      .pipe(
        takeUntil(this.ngUnsubscribe$),
        filter((data) => !isEmpty(data))
      )
      .subscribe((data) => {
        this.gatewaySerial = data.searchParams.gatewaySerial || '';
        this.boilerSerial = data.searchParams.boilerSerial || '';

        this.cdr.detectChanges();

        window.scrollTo(0, 80);

        this.search();
      });
  }

  search(): void {
    if (!this.isSearchDisabled()) {
      const resultsLimit = 10;
      const searchType =
        this.gatewaySerial.length !== 0 && this.boilerSerial.length === 0
          ? SearchType.gateway
          : SearchType.boiler;

      this.searchService.search$.next(searchType);

      if (searchType === SearchType.boiler) {
        this.searchStore.searchInstallations(this.gatewaySerial.trim(), this.boilerSerial.trim());
      } else {
        this.searchStore.searchGateways(this.gatewaySerial.trim(), resultsLimit);
      }
    }
  }

  onGatewaySerialChange(serial: string): void {
    this.gatewaySerial = serial.trim();

    if (this.gatewaySerial === '') {
      this.gatewaySerialValidationErrorPresent = false;
    } else {
      this.gatewaySerialValidationErrorPresent = !this.validateGatewaySerial();
    }
  }

  onBoilerSerialChange(serial: string): void {
    this.boilerSerial = serial.trim();

    if (this.boilerSerial === '') {
      this.boilerSerialValidationErrorPresent = false;
    } else {
      this.boilerSerialValidationErrorPresent = !this.validateBoilerSerial();
    }
  }

  isSearchDisabled(): boolean {
    const errorPresent =
      this.boilerSerialValidationErrorPresent || this.gatewaySerialValidationErrorPresent;
    const bothFieldsAreEmpty = this.boilerSerial === '' && this.gatewaySerial === '';

    return errorPresent || bothFieldsAreEmpty ? true : false;
  }

  validateGatewaySerial(): boolean {
    const isValid =
      this.gatewaySerial && this.gatewaySerial.trim() && this.serialRegExp.test(this.gatewaySerial);

    return isValid;
  }

  validateBoilerSerial(): boolean {
    const isValid =
      this.boilerSerial && this.boilerSerial.trim() && this.serialRegExp.test(this.boilerSerial);

    return isValid;
  }

  clear(): void {
    this.gatewaySerial = '';
    this.boilerSerial = '';
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
    this.searchService.gatewaySearchParams$.next(undefined);
  }
}
